import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FolderOpen, Video, FileText, Clock, Users } from 'lucide-react';
import { ProgramData } from '@/pages/add-program';

interface Step6SaveProgramProps {
  data: ProgramData;
  onSave: () => void;
}

export default function Step6SaveProgram({ data }: Step6SaveProgramProps) {
  // Calculate total duration from all videoSeries components
  const totalDuration = (data.components || []).reduce((total, component) => {
    if (component.componentType === 'videoSeries') {
      return total + component.tasks.reduce((sum, task) => sum + (task.durationMinutes || 0), 0);
    }
    return total;
  }, 0);
  
  // Count total components
  const componentCount = (data.components || []).length;

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Program Summary</h3>
        <p className="text-slate-600">
          Review your program details before saving. You can edit these details later if needed.
        </p>
      </div>

      {/* Program Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FolderOpen className="w-5 h-5 text-primary" />
            <span>Program Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Program Name</p>
              <p className="text-slate-900 mt-1">{data.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Components</p>
              <Badge variant="outline" className="mt-1">
                {componentCount} component{componentCount !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Module</p>
              <Badge variant="outline" className="mt-1 capitalize">
                {data.module}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Content Library</p>
              <p className="text-slate-900 mt-1">{data.contentLibraryName}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Program Description */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-primary" />
            <span>Program Description</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.notesGeneral && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">What is it?</p>
              <p className="text-slate-600 text-sm leading-relaxed">{data.notesGeneral}</p>
            </div>
          )}
          
          {data.notesWho && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Who is it for?</p>
                <p className="text-slate-600 text-sm leading-relaxed">{data.notesWho}</p>
              </div>
            </>
          )}
          
          {data.notesAdvice && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">How should you do it?</p>
                <p className="text-slate-600 text-sm leading-relaxed">{data.notesAdvice}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Program Components */}
      {componentCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Video className="w-5 h-5 text-primary" />
              <span>Program Components ({componentCount})</span>
            </CardTitle>
            {totalDuration > 0 && (
              <CardDescription>
                <div className="flex items-center space-x-1 text-sm mt-2">
                  <Clock className="w-4 h-4" />
                  <span>Total Video Duration: {totalDuration} minutes</span>
                </div>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(data.components || []).map((component, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Badge variant="outline" className="font-mono text-xs">
                      #{component.componentOrder}
                    </Badge>
                    <div>
                      <p className="font-medium text-slate-900 text-sm capitalize">
                        {component.componentType === 'videoSeries' && `Video Series (${component.tasks.length} videos)`}
                        {component.componentType === 'repeatingTask' && 'Repeating Task'}
                        {component.componentType === 'steps' && `Steps (${component.target} target)`}
                        {component.componentType === 'dynamicTask' && `Dynamic Task (${component.tasks.length} tasks)`}
                      </p>
                      {component.componentType === 'repeatingTask' && component.tasks.length > 0 && (
                        <p className="text-xs text-slate-500">{component.tasks.map(t => t.taskMessage).join(', ')}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Videos */}
      {(data.instructionContentId || data.conclusionContentId) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Video className="w-5 h-5 text-primary" />
              <span>Additional Videos</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-700">Instruction Video</p>
                <Badge variant={data.instructionContentId ? "default" : "secondary"} className="mt-1">
                  {data.instructionContentId ? "Selected" : "None"}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Conclusion Video</p>
                <Badge variant={data.conclusionContentId ? "default" : "secondary"} className="mt-1">
                  {data.conclusionContentId ? "Selected" : "None"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Final Instructions */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-6">
          <div className="text-center">
            <h4 className="font-semibold text-green-900 mb-2">Ready to Save</h4>
            <p className="text-green-800 text-sm mb-4">
              Your program is ready to be saved. Click the "Save Program" button below to create your program.
            </p>
            <div className="flex justify-center space-x-6 text-xs text-green-700">
              <span>✓ Program configured</span>
              <span>✓ Content organized</span>
              <span>✓ Ready to publish</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

